import { StateCreator } from 'zustand';
import {
  AbTestFlowIds,
  AppStateTypes,
  IOnboardingSlice,
  OnboardingFlowIdentifier,
  OnboardingFlowType,
  RouterData,
} from '../../../types/onboarding.types.ts';
import { checkDeviceType, checkIsFromLiliApp } from '@Services/utils/utils.ts';
import { RoutesMap } from '@Router/router.types.ts';
import { DEFAULT_PAGE_INDEX } from '@Router/router.consts.ts';

export const createOnboardingSlice: StateCreator<IOnboardingSlice> = (set, get) => ({
  onboardingSlice: {
    navigationStack: [],
    pickupWhereLeftNavigationStack: [],
    appState: AppStateTypes.PRELOAD,
    editMode: false,
    currentPageIndex: DEFAULT_PAGE_INDEX,
    progressValue: 0,
    flowType: OnboardingFlowType.REGULAR,
    isFromLiliApp: checkIsFromLiliApp(),
    isPartnerFlow: false,
    partnerName: '',
    onboardingFlowIdentifier: OnboardingFlowIdentifier.DIRECT,
    abTestFlowId: AbTestFlowIds.CONTROL,
    onboardingStartTime: Date.now(),
    deviceType: checkDeviceType(),
    partnerToken: '',
    customerKey: '',
    customerId: '',
    authorizedUserId: '',
    referralCode: '',
    sourcePlatform: '',
    adSource: '',
    isLoginProcessFinished: false,
    isPlanActivelySelected: false,
    routerData: { prevRoute: '', currentRoute: '' },
    deviceId: '',
    preSelectedAnnualPlan: false,
    isAnnualPlanSelected: false,
    setRouterData: (value: RouterData) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          routerData: value,
        },
      }),
    setIsLoginProcessFinished: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          isLoginProcessFinished: value,
        },
      }),
    setQueryParamValue: (key: string, value: string) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          [key]: value,
        },
      }),
    setAppState: (value: AppStateTypes) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          appState: value,
        },
      }),
    setNavigationStack: (value: RoutesMap[]) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          navigationStack: value,
        },
      }),
    setPickupWhereLeftNavigationStack: (value: RoutesMap[]) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          pickupWhereLeftNavigationStack: value,
        },
      }),
    setIsPartnerFlow: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          isPartnerFlow: value,
        },
      }),
    setPartnerName: (value: string) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          partnerName: value,
        },
      }),
    setOnboardingIdentifier: (value: OnboardingFlowIdentifier) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          onboardingFlowIdentifier: value,
        },
      }),
    setEditMode: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          editMode: value,
        },
      }),
    setCurrentPageIndex: (value: number) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          currentPageIndex: value,
        },
      }),
    setProgressValue: (value: number) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          progressValue: value,
        },
      }),
    setFlowType: (value: OnboardingFlowType) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          flowType: value,
        },
      }),
    setAbTestFlowId: (value: AbTestFlowIds) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          abTestFlowId: value,
        },
      }),
    getOnboardingData: () => {
      return { ...get().onboardingSlice };
    },
    getIsEmbedded: () => {
      return (
        get().onboardingSlice.onboardingFlowIdentifier === OnboardingFlowIdentifier.PARTNER_EMBEDDED
      );
    },
    setPlanActivelySelected: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          isPlanActivelySelected: value,
        },
      }),
    setDeviceId: (value: string) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          deviceId: value,
        },
      }),
    setIsAnnualPlanSelected: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          isAnnualPlanSelected: value,
        },
      }),
    setSelectedPlan: (value) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          selectedPlan: value,
        },
      }),
    setPreSelectedAnnualPlan: (value: boolean) =>
      set({
        onboardingSlice: {
          ...get().onboardingSlice,
          preSelectedAnnualPlan: value,
        },
      }),
  },
});
